import { IonAvatar, IonContent, IonHeader, IonIcon, IonItem, IonLabel, IonList, IonListHeader, IonMenuToggle, IonTitle, IonToolbar, useIonAlert } from '@ionic/react';
import { beakerOutline, beakerSharp, bugOutline, home, homeSharp, informationCircleOutline, locationOutline, logOutOutline, settings, settingsSharp } from 'ionicons/icons';
import { useHistory, useLocation as useLocationDom } from 'react-router-dom';

import { IBranch } from '../../models/dms/branch.model';
import { IWorkCategory } from '../../models/dms/technician.model';
import { ITypeCode } from '../../models/dms/typecode.model';
import { EmptyOdataResult } from '../../models/odata.model';
import { RouteNames } from '../../models/routes.model';
import { useToast } from '../../providers/ToastProvider';
import store from '../../store';
import { setIsBackgroundState, setIsDebugState } from '../../store/appSlice';
import { signOutAsync } from '../../store/authSlice';
import { setBranchesSyncStoreAsync } from '../../store/branchSyncSlice';
import { setDelayCodesSyncStoreAsync } from '../../store/delayCodeSyncSlice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setRepairOrderSyncStoreAsync } from '../../store/repairOrderSyncSlice';
import { setWorkCategoriesSyncStoreAsync } from '../../store/workCategorySyncSlice';
import { DashboardSelectedTab } from '../dashboardFooter/DashboardFooter';
import { IonMenuWrapper } from './Menu.styles';
import { getTotalDistance, setLocationOdometer } from '../../common/mapsUtil';
import simulation1 from '../../data/simulation1.json';
import { Coords, Location } from '@transistorsoft/capacitor-background-geolocation';
import { useLocation } from '../../providers/LocationProvider';

interface AppPage {
  url: string;
  iosIcon: string;
  mdIcon: string;
  title: string;
}

const primaryAppPages: AppPage[] = [
  {
    title: 'Dashboard',
    url: RouteNames.dashboard,
    iosIcon: home,
    mdIcon: homeSharp,
  },
];

const secondaryAppPages: AppPage[] = [
  {
    title: 'Settings',
    url: RouteNames.settings,
    iosIcon: settings,
    mdIcon: settingsSharp,
  },
  {
    title: 'About',
    url: RouteNames.about,
    iosIcon: informationCircleOutline,
    mdIcon: informationCircleOutline,
  },
];

let debugCount = 0;

const Menu: React.FC = () => {
  const location = useLocationDom();
  const [presentAlert] = useIonAlert();

  const { isDebug, showMainMenu, isDemo } = useAppSelector((state) => state.app);
  const { token } = useAppSelector((state) => state.auth);
  const { selectedDatabase } = useAppSelector((state) => state.database);
  const {
    actions: { errorToast },
  } = useToast();
  const {
    actions: { handleLocation },
  } = useLocation();

  const dispatch = useAppDispatch();
  const history = useHistory();

  const handleHeaderClick = () => {
    debugCount++;
    if (debugCount === 10) {
      debugCount = 0;
      presentAlert({
        header: 'Enable Debug Mode',
        backdropDismiss: false,
        inputs: [
          {
            name: 'password',
            type: 'password',
            placeholder: 'Enter debug password',
          },
        ],
        buttons: [
          {
            text: 'Cancel',
            role: 'cancel',
            cssClass: 'secondary-button',
            handler: () => {
              dispatch(setIsDebugState(false));
              debugCount = 0;
            },
          },
          {
            text: 'Ok',
            cssClass: 'secondary-button',
            handler: (val) => {
              debugCount = 0;
              if (val.password === 'abc') {
                dispatch(setIsDebugState(true));
              }
            },
          },
        ],
      });
    }
  };

  const clearSyncCache = async () => {
    // When clearing repair orders, keep any adhoc repair orders
    const adhocRos = store.getState().repairOrderSync.repairOrderSync?.data.filter((ro) => ro.isAdhoc) || [];
    await dispatch(
      setRepairOrderSyncStoreAsync({
        data: adhocRos.length ? [...adhocRos] : [],
        hasSync: false,
        isProcessing: false,
        timestamp: 0,
      }),
    );
    await dispatch(
      setDelayCodesSyncStoreAsync({
        data: EmptyOdataResult<ITypeCode>(),
        hasSync: false,
        isProcessing: false,
        timestamp: 0,
      }),
    );
    await dispatch(
      setWorkCategoriesSyncStoreAsync({
        data: EmptyOdataResult<IWorkCategory>(),
        hasSync: false,
        isProcessing: false,
        timestamp: 0,
      }),
    );
    await dispatch(
      setBranchesSyncStoreAsync({
        data: EmptyOdataResult<IBranch>(),
        hasSync: false,
        isProcessing: false,
        timestamp: 0,
      }),
    );
  };

  const simulateOdo = async () => {
    const simulation = [...simulation1];
    const distance = getTotalDistance(simulation);
    setLocationOdometer(distance);
    simulation.forEach((location) => {
      handleLocation({ coords: { ...location } as Coords } as Location, true);
    });
  };

  return (
    <IonMenuWrapper menuId="main-menu" contentId="main" type="overlay" style={showMainMenu ? {} : { display: 'none' }}>
      <IonHeader color="lightest" onClick={() => handleHeaderClick()}>
        <IonToolbar color="primary">
          <IonTitle>Tech Mobile</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent color="lightest">
        <IonList lines="none" id="inbox-list">
          <IonMenuToggle autoHide={false}>
            <IonItem lines="full" routerLink={RouteNames.profile}>
              <IonAvatar slot="start">
                <img alt="profile pic" src="assets/gravatar.png" />
              </IonAvatar>
              <IonLabel>
                {/* <app-load-test></app-load-test> */}
                <IonLabel>{token?.displayUsername || 'User'}</IonLabel>
                <IonLabel>{selectedDatabase?.dbDisplayName || ''}</IonLabel>
              </IonLabel>
            </IonItem>
          </IonMenuToggle>

          {primaryAppPages.map((appPage, index) => {
            return (
              <IonMenuToggle key={index} autoHide={false}>
                <IonItem className={location.pathname === appPage.url ? 'selected' : ''} routerLink={appPage.url} routerDirection="none" lines={index === primaryAppPages.length - 1 ? 'full' : 'none'} detail={false}>
                  <IonIcon slot="start" ios={appPage.iosIcon} md={appPage.mdIcon} />
                  <IonLabel>{appPage.title}</IonLabel>
                </IonItem>
              </IonMenuToggle>
            );
          })}
          {secondaryAppPages.map((appPage, index) => {
            return (
              <IonMenuToggle key={index} autoHide={false}>
                <IonItem className={location.pathname === appPage.url ? 'selected' : ''} routerLink={appPage.url} routerDirection="none" lines={index === secondaryAppPages.length - 1 ? 'full' : 'none'} detail={false}>
                  <IonIcon slot="start" ios={appPage.iosIcon} md={appPage.mdIcon} />
                  <IonLabel>{appPage.title}</IonLabel>
                </IonItem>
              </IonMenuToggle>
            );
          })}
        </IonList>
        {(process.env.REACT_APP_PRODUCTION === 'false' || process.env.REACT_APP_DEBUG === 'true' || isDebug) && !isDemo && (
          <IonList id="labels-list">
            <IonListHeader>Test</IonListHeader>
            <IonMenuToggle autoHide={false}>
              <IonItem className={location.pathname === RouteNames.sandbox ? 'selected' : ''} routerLink={RouteNames.sandbox} routerDirection="none" lines="none" detail={false}>
                <IonIcon slot="start" ios={beakerOutline} md={beakerSharp} />
                <IonLabel>Sandbox</IonLabel>
              </IonItem>
              <IonItem className={location.pathname === RouteNames.debugLog ? 'selected' : ''} routerLink={RouteNames.debugLog} routerDirection="none" lines="none" detail={false}>
                <IonIcon slot="start" ios={bugOutline} md={bugOutline} />
                <IonLabel>Debug Log</IonLabel>
              </IonItem>
              {/* <IonItem lines="none" detail={false} onClick={() => scheduleTestNow()}>
                <IonIcon slot="start" ios={locationOutline} md={locationOutline} />
                <IonLabel>Test Notification Now</IonLabel>
              </IonItem>
              <IonItem lines="none" detail={false} onClick={() => scheduleTest1Sec()}>
                <IonIcon slot="start" ios={locationOutline} md={locationOutline} />
                <IonLabel>Test Notification in 1 Second</IonLabel>
              </IonItem> */}
              <IonItem lines="none" detail={false} onClick={() => clearSyncCache()}>
                <IonIcon slot="start" ios={locationOutline} md={locationOutline} />
                <IonLabel>Clear Sync Cache</IonLabel>
              </IonItem>
              <IonMenuToggle autoHide={false}>
                <IonItem
                  onClick={(e) => {
                    e.preventDefault();
                    history.push({
                      pathname: RouteNames.demo,
                      search: `tab=${DashboardSelectedTab.ClockingSummary}`,
                    });
                  }}
                  lines="none">
                  <IonIcon slot="start" ios={beakerOutline} md={beakerOutline} />
                  <IonLabel>Demo</IonLabel>
                </IonItem>
              </IonMenuToggle>
              <IonItem
                lines="none"
                onClick={() => {
                  dispatch(signOutAsync());
                }}>
                <IonIcon slot="start" ios={logOutOutline} md={logOutOutline} />
                <IonLabel>Sign Out</IonLabel>
              </IonItem>
              <IonItem
                lines="none"
                onClick={() => {
                  try {
                    // Try killing the app
                    (window.cordova.plugins as any).exit();
                  } catch {
                    /*ignore errors*/
                  }
                }}>
                <IonIcon slot="start" ios={logOutOutline} md={logOutOutline} />
                <IonLabel>Kill App</IonLabel>
              </IonItem>
              <IonItem lines="none" onClick={() => dispatch(setIsBackgroundState(true))}>
                <IonIcon slot="start" ios={logOutOutline} md={logOutOutline} />
                <IonLabel>Set To Background Mode</IonLabel>
              </IonItem>
              <IonItem lines="none" onClick={() => errorToast('Test Toast')}>
                <IonIcon slot="start" ios={logOutOutline} md={logOutOutline} />
                <IonLabel>Toast</IonLabel>
              </IonItem>
              <IonItem lines="none" onClick={() => simulateOdo()}>
                <IonIcon slot="start" ios={locationOutline} md={locationOutline} />
                <IonLabel>Simulate Odo</IonLabel>
              </IonItem>
            </IonMenuToggle>
          </IonList>
        )}
      </IonContent>
    </IonMenuWrapper>
  );
};

export default Menu;
